<template>
  <div v-if="car">
    <section class="site_container car">
      <SiteCarousel :slides="car.gallery" class="car_gallery" />

      <div>
        <nav class="car_crumbs" aria-label="Okruszki">
          <router-link to="/">Strona główna</router-link>
          <router-link to="/samochody/">Samochody</router-link>
          <span>{{ car.name }}</span>
        </nav>

        <h1>{{ car.name }}</h1>
        <p class="car_year">{{ car.year }}</p>

        <dl class="car_specs">
          <div>
            <dt>Przebieg</dt>
            <dd>{{ car.mileage }}</dd>
          </div>
          <div>
            <dt>Moc</dt>
            <dd>{{ car.power }}</dd>
          </div>
          <div>
            <dt>Skrzynia</dt>
            <dd>{{ car.gearbox }}</dd>
          </div>
        </dl>

        <div class="car_buy">
          <div>
            <p class="car_label">Cena brutto</p>
            <p class="car_price">{{ car.price }}</p>
          </div>
          <a href="#kontakt" class="site_button">Zapytaj o samochód <ArrowIcon /></a>
        </div>

        <SiteContactList
          class="car_contacts"
          :items="[
            { icon: 'phone', title: 'Numer telefonu', value: car.phone, href: `tel:${car.phone.replace(/\s/g, '')}` },
            { icon: 'pin', title: 'Lokalizacja biura', value: car.address },
          ]"
        />
      </div>
    </section>

    <section class="site_container car_spec">
      <h2>Pełna specyfikacja</h2>
      <p>{{ car.specification }}</p>
    </section>

    <section id="kontakt" class="site_section site_section--paper">
      <div class="site_container">
        <SiteEnquiry />
      </div>
    </section>
  </div>
</template>

<script>
// One car from stock. The route carries the slug; the data lives in
// src/data/cars.js.

import pageHead from '@/mixins/pageHead'
import { pageSchema } from '@/utils/schema'
import cars from '@/data/cars'
import SiteCarousel from '@/components/SiteCarousel.vue'
import SiteContactList from '@/components/SiteContactList.vue'
import SiteEnquiry from '@/components/SiteEnquiry.vue'
import ArrowIcon from '@/components/icons/ArrowIcon.vue'

export default {
  name: 'CarPage',
  components: { SiteCarousel, SiteContactList, SiteEnquiry, ArrowIcon },
  mixins: [pageHead],

  // Resolved before the component is created, so the mixin can read it.
  beforeRouteEnter(to, from, next) {
    const car = cars.find((item) => item.slug === to.params.slug)
    next(car ? undefined : '/samochody/')
  },

  computed: {
    car() {
      return cars.find((item) => item.slug === this.$route.params.slug)
    },
  },

  pageHead() {
    const car = this.car
    if (!car) return {}

    return {
      title: `${car.name} - Japonia Cars`,
      description: `${car.name} sprowadzone z Japonii — dostępne od ręki w salonie Japonia Cars.`,
      schema: pageSchema({
        path: car.path,
        name: car.name,
        image: car.cover,
        breadcrumbs: [{ name: 'Samochody', path: '/samochody/' }, { name: car.name }],
        published: '2026-05-12T05:53:20+00:00',
        modified: '2026-05-12T05:53:20+00:00',
      }),
    }
  },
}
</script>

<style scoped>
.car {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 74px;
  padding-top: 48px;
  padding-bottom: 80px;
}

.car_crumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.car_crumbs a {
  color: var(--accent);
}

.car_crumbs a::after {
  content: ' /';
  color: var(--ink-body);
}

.car h1 {
  margin: 20px 0 16px;
  font-size: 50px;
}

.car_year {
  margin-bottom: 16px;
}

.car_specs {
  display: grid;
  grid-template-columns: repeat(3, 190px);
  gap: 32px;
  margin: 16px 0;
  padding: 24px 0;
  border-top: 1px solid var(--paper-line);
  border-bottom: 1px solid var(--paper-line);
}

.car_specs dt,
.car_label {
  color: rgba(10, 10, 11, 0.61);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.car_specs dd {
  margin: 0;
}

.car_buy {
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 8px;
}

.car_price {
  color: var(--ink);
  font-size: 32px;
  font-weight: 600;
}

.car_contacts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 24px;
}

.car_contacts :deep(.contact_icon) {
  width: 62px;
  height: 62px;
  font-size: 32px;
}

.car_contacts :deep(li + li) {
  border-top: 0;
}

.car_gallery :deep(.carousel_stage img) {
  aspect-ratio: 3 / 2;
}

.car_spec {
  padding-top: 24px;
  padding-bottom: 64px;
}

.car_spec h2 {
  margin-bottom: 22.5px;
}

@media (max-width: 1024px) {
  .car {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .car_specs {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .car_buy,
  .car_contacts {
    flex-direction: column;
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>
